// Vendors
import { VARIANTS } from '@openbank/ziniaweb-ui-components';
// Constants
import {
  ERROR_COMPONENT_DECLINED_IMAGE,
  ERROR_COMPONENT_DEFAULT_IMAGE,
  ERROR_COMPONENT_STATES_IMAGE,
  ERROR_CANT_CONTINUE_IMAGE,
  UPLOAD_ID_CAMERA_FAILURE_IMAGE,
} from './constants/error.configurations.constants';
// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
import { AppStatesEnumeration } from 'enumerations/app-states.enumeration';
// Event Handlers
import { customerCantContinueAndHasToReturnAmazon } from 'providers/error/handlers/utils/error.handlers.utils';
// Translations
import { DESCRIPTIONS, TITLES } from '../translations/error.translations';
import {
  BUTTON_RETRY,
  BUTTON_RETURN_AMAZON,
  ONBOARDING_FLOW_DESCRIPTIONS,
  ONBOARDING_FLOW_TITLES,
  UPLOAD_ID_BIZUM_ERROR,
  UPLOAD_ID_CAMERA_FAILURE_ERROR,
} from './translations/error.configurations.translations';
// Types
import type { ErrorType } from '../types/error.type';
// Utilities
import {
  isContractError,
  onboardingTimeLimeHasReached,
  isKycError,
  isOnboardingFlowError,
  isPaymentError,
  isProofIncomeError,
  isUploadIdError,
  isUploadIdCameraFailureError,
  isUploadIdBizumError,
} from '../utils/error.utils';

const getErrorImage = (error: ErrorType['error']): string => {
  switch (true) {
    case onboardingTimeLimeHasReached(error):
      return ERROR_COMPONENT_DECLINED_IMAGE;
    case isOnboardingFlowError(error) && !isUploadIdCameraFailureError(error):
      return ERROR_COMPONENT_STATES_IMAGE;
    case isUploadIdCameraFailureError(error):
      return UPLOAD_ID_CAMERA_FAILURE_IMAGE;
    case customerCantContinueAndHasToReturnAmazon(error as AppErrorEnumeration):
      return ERROR_CANT_CONTINUE_IMAGE;
    default:
      return ERROR_COMPONENT_DEFAULT_IMAGE;
  }
};

const getButtonErrorLabel = (error: AppErrorEnumeration): string =>
  customerCantContinueAndHasToReturnAmazon(error) || onboardingTimeLimeHasReached(error)
    ? BUTTON_RETURN_AMAZON
    : BUTTON_RETRY;

const getErrorTitle = (error: ErrorType['error']): string => {
  switch (true) {
    case isContractError(error):
      return ONBOARDING_FLOW_TITLES[AppStatesEnumeration.CHECKOUT_SIGNING];
    case isKycError(error):
      return ONBOARDING_FLOW_TITLES[AppStatesEnumeration.CHECKOUT_IDENTIFICATION];
    case isPaymentError(error):
      return ONBOARDING_FLOW_TITLES[AppStatesEnumeration.CHECKOUT_DOWNPAYMENT];
    case isProofIncomeError(error):
      return ONBOARDING_FLOW_TITLES[AppStatesEnumeration.CHECKOUT_INCOME];
    case isUploadIdError(error) && !isUploadIdCameraFailureError(error):
      return ONBOARDING_FLOW_TITLES[AppStatesEnumeration.CHECKOUT_PENDING];
    case isUploadIdCameraFailureError(error):
      return UPLOAD_ID_CAMERA_FAILURE_ERROR.TITLE;
    default:
      return TITLES[error as string];
  }
};

const getErrorDescription = (error: ErrorType['error']): string => {
  switch (true) {
    case isContractError(error):
      return ONBOARDING_FLOW_DESCRIPTIONS[AppStatesEnumeration.CHECKOUT_SIGNING];
    case isKycError(error):
      return ONBOARDING_FLOW_DESCRIPTIONS[AppStatesEnumeration.CHECKOUT_IDENTIFICATION];
    case isPaymentError(error):
      return ONBOARDING_FLOW_DESCRIPTIONS[AppStatesEnumeration.CHECKOUT_DOWNPAYMENT];
    case isProofIncomeError(error):
      return ONBOARDING_FLOW_DESCRIPTIONS[AppStatesEnumeration.CHECKOUT_INCOME];
    case isUploadIdError(error) &&
      !isUploadIdCameraFailureError(error) &&
      !isUploadIdBizumError(error):
      return ONBOARDING_FLOW_DESCRIPTIONS[AppStatesEnumeration.CHECKOUT_PENDING];
    case isUploadIdCameraFailureError(error):
      return UPLOAD_ID_CAMERA_FAILURE_ERROR.DESCRIPTION;
    case isUploadIdBizumError(error):
      return UPLOAD_ID_BIZUM_ERROR.DESCRIPTION;
    default:
      return DESCRIPTIONS[error as string];
  }
};

const getFooterVariant = (
  error: ErrorType['error']
):
  | typeof VARIANTS.FooterVariantsType.ACTION_BUTTONS
  | typeof VARIANTS.FooterVariantsType.ONE_ACTION =>
  isOnboardingFlowError(error)
    ? VARIANTS.FooterVariantsType.ACTION_BUTTONS
    : VARIANTS.FooterVariantsType.ONE_ACTION;

export { getErrorDescription, getButtonErrorLabel, getErrorImage, getErrorTitle, getFooterVariant };
